import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase.js';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';

const CalendarEventPage = () => {
  const { dealId } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'initial';
  const updateId = searchParams.get('u');
  
  const [dealData, setDealData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load deal data for display
    loadDealData();
  }, [dealId]);

  const loadDealData = async () => {
    try {
      let milestones = [];
      let dealInfo = null;

      if (mode === 'update' && updateId) {
        // Load from update snapshot
        const { data: snapshot, error: snapshotError } = await supabase
          .from('update_snapshots')
          .select('milestone_ids, deal_id')
          .eq('id', updateId)
          .single();

        if (snapshotError) throw snapshotError;

        const { data: snapshotMilestones, error: milestonesError } = await supabase
          .from('milestones')
          .select('*, deals!inner(*)')
          .in('id', snapshot.milestone_ids);

        if (milestonesError) throw milestonesError;
        
        milestones = snapshotMilestones;
        dealInfo = snapshotMilestones[0]?.deals;
      } else {
        // Load all active milestones for deal
        const { data: allMilestones, error: milestonesError } = await supabase
          .from('milestones')
          .select('*, deals!inner(*)')
          .eq('deal_id', dealId)
          .eq('status', 'active');

        if (milestonesError) throw milestonesError;
        
        milestones = allMilestones;
        dealInfo = allMilestones[0]?.deals;
      }

      // Convert to expected format
      const dates = {};
      milestones.forEach(milestone => {
        dates[milestone.type] = {
          value: new Date(milestone.start_dt).toLocaleDateString()
        };
      });

      setDealData({
        id: dealId,
        extractedData: {
          property: {
            address: dealInfo ? `${dealInfo.property_street}, ${dealInfo.city}` : 'Property'
          },
          dates
        }
      });
    } catch (error) {
      console.error('Error loading deal data:', error);
      // Fallback to localStorage for offline contracts
      try {
        // Try multiple localStorage keys
        let contract = null;
        
        // First try the latest contract data
        const latestData = localStorage.getItem('latestContractData');
        if (latestData) {
          contract = JSON.parse(latestData);
        }
        
        // If not found, try processedContracts array
        if (!contract) {
          const contracts = JSON.parse(localStorage.getItem('processedContracts') || '[]');
          contract = contracts.find(c => c.id === dealId || c.contract_filename?.includes(dealId));
        }
        
        // If still not found, try looking for any recent contract data
        if (!contract) {
          const contractParserData = localStorage.getItem('contractParser_offlineContracts');
          if (contractParserData) {
            const offlineContracts = JSON.parse(contractParserData);
            contract = offlineContracts[0]; // Use most recent
          }
        }
        
        if (contract && contract.extractedData) {
          setDealData(contract);
        } else {
          // Create demo data for testing
          setDealData({
            id: dealId,
            extractedData: {
              property: { address: 'Demo Property' },
              dates: {
                closingDate: { value: '12/30/2024' },
                inspectionDeadline: { value: '12/15/2024' },
                financingDeadline: { value: '12/20/2024' }
              }
            }
          });
        }
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
        // Create demo data
        setDealData({
          id: dealId,
          extractedData: {
            property: { address: 'Demo Property' },
            dates: {
              closingDate: { value: '12/30/2024' },
              inspectionDeadline: { value: '12/15/2024' },
              financingDeadline: { value: '12/20/2024' }
            }
          }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToGoogle = () => {
    // Generate Google Calendar links for ALL dates
    if (!dealData?.extractedData?.dates) {
      alert('No calendar data available');
      return;
    }

    const dates = dealData.extractedData.dates;
    const property = dealData.extractedData.property;

    // Get all available dates
    const allDates = Object.entries(dates).filter(([key, dateInfo]) => dateInfo?.value);

    if (allDates.length === 0) {
      alert('No dates available to add to calendar');
      return;
    }

    // Open multiple calendar tabs - one for each date
    allDates.forEach(([dateKey, dateInfo], index) => {
      const eventTitle = `${property.address || 'Contract Property'} - ${dateKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`;

      try {
        // Parse and format date for Google Calendar
        const eventDate = new Date(dateInfo.value);
        const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
        const endDate = startDate;

        const params = new URLSearchParams({
          action: 'TEMPLATE',
          text: eventTitle,
          dates: `${startDate}/${endDate}`,
          details: `Contract deadline: ${dateKey.replace(/([A-Z])/g, ' $1')}`,
          location: property.address || '',
          ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

        // Slight delay between opening tabs to prevent popup blocker
        setTimeout(() => {
          window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
        }, index * 300);
      } catch (error) {
        console.error(`Error creating calendar link for ${dateKey}:`, error);
      }
    });

    // Show confirmation
    alert(`Opening ${allDates.length} calendar event${allDates.length > 1 ? 's' : ''}. Please allow pop-ups if blocked.`);
  };

  const handleDownloadICS = () => {
    // Generate ICS file directly from local data
    if (!dealData?.extractedData?.dates) {
      alert('No calendar data available');
      return;
    }

    const dates = dealData.extractedData.dates;
    const property = dealData.extractedData.property;
    
    // Generate events for all available dates
    const events = [];
    Object.entries(dates).forEach(([key, dateInfo]) => {
      if (dateInfo?.value) {
        const eventTitle = `${property.address || 'Contract Property'} - ${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`;
        events.push({
          title: eventTitle,
          date: dateInfo.value,
          description: `Contract deadline: ${key.replace(/([A-Z])/g, ' $1')}`
        });
      }
    });

    if (events.length === 0) {
      alert('No dates available to create calendar file');
      return;
    }

    try {
      // Generate ICS content
      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Contract Parser//Contract Dates//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
${events.map((event, index) => {
  const eventDate = new Date(event.date);
  const dateStr = eventDate.toISOString().split('T')[0].replace(/-/g, '');
  
  return `BEGIN:VEVENT
UID:${Date.now()}-${index}@contractparser.pro
SUMMARY:${event.title}
DTSTART;VALUE=DATE:${dateStr}
DTEND;VALUE=DATE:${dateStr}
DTSTAMP:${timestamp}
DESCRIPTION:${event.description}
SEQUENCE:0
STATUS:CONFIRMED
END:VEVENT`;
}).join('\n')}
END:VCALENDAR`;

      // Download the file
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${dealId}-events.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating ICS file:', error);
      alert('Error creating calendar file');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Calendar" size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Loading calendar event...</p>
        </div>
      </div>
    );
  }

  const property = dealData?.extractedData?.property || {};
  const dates = dealData?.extractedData?.dates || {};
  const milestoneCount = Object.keys(dates).filter(key => dates[key]?.value).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Calendar" size={32} className="text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Add Contract Events to Calendar
            </h1>
            <p className="text-gray-600">
              {property.address && (
                <>
                  <span className="font-medium">{property.address}</span>
                  <br />
                </>
              )}
              {milestoneCount} important milestone{milestoneCount !== 1 ? 's' : ''} ready to add
              {mode === 'update' && (
                <span className="block mt-1 text-sm text-orange-600 font-medium">
                  📝 Updated milestones
                </span>
              )}
            </p>
          </div>

          {milestoneCount > 0 && (
            <div className="space-y-4 mb-8">
              {Object.entries(dates)
                .filter(([_, dateInfo]) => dateInfo?.value)
                .map(([key, dateInfo]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </h3>
                      <p className="text-sm text-gray-600">{dateInfo.value}</p>
                    </div>
                    <Icon name="Calendar" size={20} className="text-gray-400" />
                  </div>
                ))
              }
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleAddToGoogle}
              className="w-full bg-black hover:opacity-90 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-opacity focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              <Icon name="Calendar" size={20} className="mr-2" />
              Add to Google Calendar
            </button>
            
            <button
              onClick={handleDownloadICS}
              className="w-full border-2 border-gray-300 hover:bg-gray-50 text-gray-700 py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-colors"
            >
              <Icon name="Download" size={20} className="mr-2" />
              Download ICS (Apple/Outlook)
            </button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <Icon name="Info" size={16} className="inline mr-1" />
              Events include 1-day advance reminders and will sync across your devices.
              {mode === 'update' && ' Updated events will replace previous versions.'}
            </p>
          </div>

          {milestoneCount === 0 && (
            <div className="text-center py-8">
              <Icon name="Calendar" size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No milestones found for this contract.</p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 text-blue-600 hover:text-blue-800 transition-colors"
              >
                ← Go Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarEventPage;